/**
 * CameraController (deadzone follow, rect bounds, min viewport sizing, focus changes, manual pan) and
 * ParallaxBackground / ParallaxPanel. These run in LateUpdate (per rendered frame) like in Unity; the
 * view only supplies the aspect ratio and reads `pos` / `orthoSize`.
 */
import { GameBehaviour, type Game } from './game.ts';
import type { PlayerController } from './player.ts';
import { PlayerState, PowerupType } from './player.ts';
import { bool, num } from './props.ts';
import { moveTowardsV, smoothDamp, smoothDampV, vlen, type FrameCounter, type V2 } from './unity.ts';
import { spriteBounds } from './mechanics/util.ts';

/** Right-stick / IJKL below this is rest noise, not a pan. Unity's .normalized made 0.01 into full speed. */
export const CAMERA_PAN_DEADZONE = 0.25;

/** CameraPreviewHandler as seen by the camera */
export interface CameraLookAt {
  lookAtPosition: V2;
  pauseAtDestinationCounter: FrameCounter;
  getCameraPreviewMoveSpeed(): number;
}

export const enum CameraPreviewState {
  MovingTowards,
  Pausing,
  MovingBack,
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export class CameraController extends GameBehaviour {
  override noSave = ['parallax'];
  changeFocusSpeed = 150;
  changeFocusSmoothTime = 0.3;
  cameraLookAtSmoothTime = 0.3;
  cameraPaddingLookAtPlayer = 1;
  lookAtHandler: CameraLookAt | null = null;
  lookAtState = CameraPreviewState.MovingTowards;
  useRectBounds = false;
  cameraMaxBounds: Rect = { x: 0, y: 0, w: 0, h: 0 };
  horizontalPadding = 13;
  verticalPadding = 8;
  cameraPanAcceleration = 80;
  cameraPanSpeed = 20;
  lockCameraX = false;
  lockCameraY = false;
  minViewportTiles: V2 = { x: 42, y: 24 };
  overrideCameraSize = 0;
  bigDudeCameraScale = 1.25;
  cameraSizeChangeSpeed = 9;
  cameraSizeChangeSmoothTime = 0.3;

  /** camera transform position (z is irrelevant in 2D) */
  pos: V2 = { x: 0, y: 0 };
  orthoSize = 12;
  desiredCameraSize = 12;
  cameraSizeChangeVelocity = 0;
  lastOrthoAspect = -1;
  changingPlayerFocus = false;
  smoothDampVelocity: V2 = { x: 0, y: 0 };
  focusedPlayer: PlayerController | null = null;
  cameraVelocity: V2 = { x: 0, y: 0 };
  camHeight = 12;
  camWidth = 21;
  parallax: ParallaxBackground | null = null;

  override awake() {
    const d = this.data;
    this.changeFocusSpeed = num(d.changeFocusSpeed, 150);
    this.changeFocusSmoothTime = num(d.changeFocusSmoothTime, 0.3);
    this.cameraLookAtSmoothTime = num(d.cameraLookAtSmoothTime, 0.3);
    this.useRectBounds = bool(d.useRectBounds);
    const b = d.cameraMaxBounds ?? {};
    this.cameraMaxBounds = { x: num(b.x), y: num(b.y), w: num(b.width), h: num(b.height) };
    this.horizontalPadding = num(d.horizontalPadding, 13);
    this.verticalPadding = num(d.verticalPadding, 8);
    this.lockCameraX = bool(d.lockCameraX);
    this.lockCameraY = bool(d.lockCameraY);
    this.minViewportTiles = { x: num(d.minViewportTiles?.x, 42), y: num(d.minViewportTiles?.y, 24) };
    this.overrideCameraSize = num(d.overrideCameraSize);
    this.bigDudeCameraScale = num(d.bigDudeCameraScale, 1.25);
    this.cameraSizeChangeSpeed = num(d.cameraSizeChangeSpeed, 9);
    this.cameraSizeChangeSmoothTime = num(d.cameraSizeChangeSmoothTime, 0.3);
    this.game.camera = this;
  }

  override start() {
    this.desiredCameraSize = this.effectiveStandardOrthographicSize();
    this.orthoSize = this.desiredCameraSize;
    this.lastOrthoAspect = this.game.viewAspect;
    // the editor branch of CameraController.Start: begin at the first spawn point
    const p = this.go.position;
    const sp = this.game.firstSpawnPoint() ?? p;
    this.pos = { x: this.lockCameraX ? p.x : sp.x, y: this.lockCameraY ? p.y : sp.y };
  }

  focusOnPlayer(p: PlayerController | null) {
    this.focusedPlayer = p;
    if (p) this.changingPlayerFocus = true;
  }

  override lateUpdate(dt: number) {
    const game = this.game;
    if (!this.focusedPlayer || this.focusedPlayer.go.destroyed) this.focusedPlayer = (game.players[0] as PlayerController | undefined) ?? null;

    let desired: V2 = { x: 0, y: 0 };
    let bigDudeZoom = false;
    for (const gp of game.players) {
      const p = gp as PlayerController;
      if (p.isUsingControlPanel || p.input.controlEnabled || p.isUsingDebugControl) continue;
      const stick = p.input.control;
      // StickDeadzone still leaves a trickle; Unity then .normalized that to full pan speed.
      if (vlen(stick) >= CAMERA_PAN_DEADZONE) desired = { x: desired.x + stick.x, y: desired.y + stick.y };
      if (p.powerup?.type === PowerupType.BigDude) bigDudeZoom = true;
    }
    this.desiredCameraSize = this.effectiveStandardOrthographicSize() * (bigDudeZoom ? this.bigDudeCameraScale : 1);

    const aspect = game.viewAspect;
    if (this.lastOrthoAspect < 0 || Math.abs(aspect - this.lastOrthoAspect) > 1e-6) {
      this.orthoSize = this.desiredCameraSize;
      this.cameraSizeChangeVelocity = 0;
      this.lastOrthoAspect = aspect;
    } else if (this.orthoSize !== this.desiredCameraSize) {
      const v = { v: this.cameraSizeChangeVelocity };
      this.orthoSize = smoothDamp(this.orthoSize, this.desiredCameraSize, v, this.cameraSizeChangeSmoothTime, this.cameraSizeChangeSpeed, dt);
      this.cameraSizeChangeVelocity = v.v;
    }

    const mag = vlen(desired);
    const pan = mag < CAMERA_PAN_DEADZONE ? { x: 0, y: 0 } : { x: (desired.x / mag) * Math.min(1, mag) * this.cameraPanSpeed, y: (desired.y / mag) * Math.min(1, mag) * this.cameraPanSpeed };
    this.cameraVelocity = moveTowardsV(this.cameraVelocity, pan, this.cameraPanAcceleration * dt);
    let cam = { ...this.pos };
    if (this.cameraVelocity.x !== 0 || this.cameraVelocity.y !== 0) cam = { x: cam.x + this.cameraVelocity.x * dt, y: cam.y + this.cameraVelocity.y * dt };

    this.camHeight = this.orthoSize;
    this.camWidth = aspect * this.camHeight;

    const fp = this.focusedPlayer;
    const la = this.lookAtHandler;
    const almostEqual = (a: V2, b: V2) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2 < 0.001;
    if (la) {
      if (this.lookAtState === CameraPreviewState.MovingTowards) {
        cam = smoothDampV(cam, la.lookAtPosition, this.smoothDampVelocity, this.cameraLookAtSmoothTime, la.getCameraPreviewMoveSpeed(), dt);
        if (almostEqual(cam, la.lookAtPosition)) {
          this.lookAtState = CameraPreviewState.Pausing;
          la.pauseAtDestinationCounter.restart();
        }
      } else if (this.lookAtState === CameraPreviewState.Pausing) {
        if (la.pauseAtDestinationCounter.countCheckFinishReset()) this.lookAtState = CameraPreviewState.MovingBack;
      } else if (fp) {
        const target = fp.go.position;
        cam = smoothDampV(cam, target, this.smoothDampVelocity, this.cameraLookAtSmoothTime, la.getCameraPreviewMoveSpeed(), dt);
        if (almostEqual(cam, target)) this.removeLookAtHandler();
      }
    } else if (fp && fp.playerState === PlayerState.Playing) {
      const pp = fp.go.position;
      const cw = this.camWidth, ch = this.camHeight, hp = this.horizontalPadding, vp = this.verticalPadding;
      if (pp.x > cam.x + cw - hp && !this.changingPlayerFocus) {
        cam.x = pp.x - cw + hp;
        this.cameraVelocity.x = 0;
      }
      if (pp.x < cam.x - cw + hp && !this.changingPlayerFocus) {
        cam.x = pp.x + cw - hp;
        this.cameraVelocity.x = 0;
      }
      if (pp.y > cam.y + ch - vp && !this.changingPlayerFocus) {
        cam.y = pp.y - ch + vp;
        this.cameraVelocity.y = 0;
      }
      if (pp.y < cam.y - ch + vp && !this.changingPlayerFocus) {
        cam.y = pp.y + ch - vp;
        this.cameraVelocity.y = 0;
      }
    }

    if (this.changingPlayerFocus && fp && !this.lookAtHandler) {
      const target = fp.go.position;
      const adjusted = { ...target };
      this.keepCameraInBounds(adjusted, { x: 0, y: 0 });
      if (this.lockCameraX) adjusted.x = this.pos.x;
      if (this.lockCameraY) adjusted.y = this.pos.y;
      if (Math.hypot(adjusted.x - cam.x, adjusted.y - cam.y) > this.cameraPaddingLookAtPlayer) {
        cam = smoothDampV(cam, target, this.smoothDampVelocity, this.changeFocusSmoothTime, this.changeFocusSpeed, dt);
      } else {
        this.changingPlayerFocus = false;
        this.smoothDampVelocity = { x: 0, y: 0 };
      }
    }

    if (this.useRectBounds) this.keepCameraInBounds(cam, this.cameraVelocity);
    this.pos = { x: this.lockCameraX ? this.pos.x : cam.x, y: this.lockCameraY ? this.pos.y : cam.y };

    this.parallax?.updatePanels(dt);
  }

  setLookAtHandler(h: CameraLookAt) {
    if (this.lookAtHandler) return;
    this.lookAtHandler = h;
    this.lookAtState = CameraPreviewState.MovingTowards;
  }

  removeLookAtHandler() {
    if (!this.lookAtHandler) return;
    this.lookAtHandler.pauseAtDestinationCounter.reset();
    this.lookAtHandler = null;
  }

  keepCameraInBounds(cam: V2, vel: V2) {
    if (!this.useRectBounds) return;
    const b = this.cameraMaxBounds;
    const xMax = b.x + b.w, yMax = b.y + b.h;
    if (cam.x + this.camWidth > xMax) {
      cam.x = xMax - this.camWidth;
      vel.x = 0;
    }
    if (cam.x - this.camWidth < b.x) {
      cam.x = b.x + this.camWidth;
      vel.x = 0;
    }
    if (cam.y + this.camHeight > yMax) {
      cam.y = yMax - this.camHeight;
      vel.y = 0;
    }
    if (cam.y - this.camHeight < b.y) {
      cam.y = b.y + this.camHeight;
      vel.y = 0;
    }
  }

  getMinOrthographicSize(aspect: number): number {
    aspect = Math.max(aspect, 0.01);
    return Math.max(this.minViewportTiles.y * 0.5, this.minViewportTiles.x / (2 * aspect));
  }

  effectiveStandardOrthographicSize(): number {
    if (this.overrideCameraSize > 0) return this.overrideCameraSize;
    return this.getMinOrthographicSize(this.game.viewAspect);
  }
}

export class ParallaxPanel extends GameBehaviour {
  static override checkpointable = false;
  xDepth = 0.8;
  xOffset = 0;
  yOffset = 0;
  xSpeed = 0;
  xPos = 0;
  spriteWidth = 1;
  spriteHeight = 1;

  override awake() {
    const d = this.data;
    this.xDepth = num(d.xDepth, 0.8);
    this.xOffset = num(d.xOffset);
    this.yOffset = num(d.yOffset);
    this.xSpeed = num(d.xSpeed);
    const b = spriteBounds(this.go);
    if (b) {
      this.spriteWidth = b[2] - b[0] || 1;
      this.spriteHeight = b[3] - b[1];
    }
  }

  updatePosition(dt: number, camPos: V2, camSize: V2, yPos: number, xMasterOffset: number) {
    this.xPos += dt * this.xSpeed * this.xDepth;
    const leftCamPos = camPos.x - camSize.x / 2;
    const bgScaledLeft = leftCamPos * this.xDepth - this.xOffset - xMasterOffset + this.xPos;
    const offset = Math.floor((leftCamPos - bgScaledLeft) / this.spriteWidth);
    this.go.position = { x: bgScaledLeft + offset * this.spriteWidth + this.spriteWidth / 2, y: yPos + this.yOffset };
    this.go.renderCopies = [{ x: this.spriteWidth - 0.01, y: 0 }];
  }
}

export class ParallaxBackground extends GameBehaviour {
  static override checkpointable = false;
  xOffset = 0;
  yOffset = 0;
  panels: ParallaxPanel[] = [];
  camera: CameraController | null = null;

  override awake() {
    this.xOffset = num(this.data.xOffset);
    this.yOffset = num(this.data.yOffset);
  }

  override start() {
    this.camera = this.go.getBehaviour(CameraController);
    if (this.camera) this.camera.parallax = this;
    const g = this.game as Game;
    this.panels = g.parallaxRoot ? g.parallaxRoot.getBehavioursInChildren(ParallaxPanel) : [];
  }

  updatePanels(dt: number) {
    const cam = this.camera;
    if (!cam) return;
    const b = cam.cameraMaxBounds;
    const h = cam.orthoSize * 2;
    const w = h * this.game.viewAspect;
    // cam.rect.height is the normalized viewport height (1), as in the original
    const camPercentY = (cam.pos.y - b.y - h / 2) / (b.h - 1);
    for (const p of this.panels) {
      const ph = p.spriteHeight;
      p.updatePosition(dt, cam.pos, { x: w, y: h }, (b.h - ph / 2) * camPercentY + b.y + ph / 2 - this.yOffset, this.xOffset);
    }
  }
}
